import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

interface BrandSettingsData {
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  background_color?: string;
  additional_colors?: string[];
  primary_font?: string;
  secondary_font?: string;
  additional_fonts?: string[];
  brand_voice_tone?: string;
  brand_voice_description?: string;
  key_messages?: string[];
  target_audience_description?: string;
}

const popularFonts = [
  'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana',
  'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
  'Playfair Display', 'Merriweather', 'Raleway', 'Inter'
];

const BrandSettings: React.FC = () => {
  const { token, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [sampleContent, setSampleContent] = useState('');
  const [showAIAnalyzer, setShowAIAnalyzer] = useState(false);
  const [settings, setSettings] = useState<BrandSettingsData>({
    primary_color: '#667eea',
    secondary_color: '#764ba2',
    accent_color: '#f59e0b',
    primary_font: 'Roboto'
  });

  useEffect(() => {
    fetchBrandSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBrandSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/brand-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.brandSettings) {
        setSettings(response.data.brandSettings);
      }
    } catch (error: any) {
      console.error('Error fetching brand settings:', error);
      // Don't show error for 404 (no settings yet)
      if (error.response?.status !== 404) {
        toast.error('Failed to load brand settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/brand-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'Brand settings saved successfully!');
    } catch (error: any) {
      console.error('Error saving brand settings:', error);
      if (error.response?.data?.requiresUpgrade) {
        toast.error(error.response.data.error);
      } else {
        toast.error(error.response?.data?.error || 'Failed to save brand settings');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleColorChange = (field: keyof BrandSettingsData, value: string) => {
    setSettings({ ...settings, [field]: value });
  };

  const addKeyMessage = () => {
    const messages = settings.key_messages || [];
    setSettings({ ...settings, key_messages: [...messages, ''] });
  };

  const updateKeyMessage = (index: number, value: string) => {
    const messages = [...(settings.key_messages || [])];
    messages[index] = value;
    setSettings({ ...settings, key_messages: messages });
  };

  const removeKeyMessage = (index: number) => {
    const messages = [...(settings.key_messages || [])];
    messages.splice(index, 1);
    setSettings({ ...settings, key_messages: messages });
  };

  const analyzeWithAI = async () => {
    if (!sampleContent.trim()) {
      toast.error('Please provide some sample content to analyze');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(
        `${API_URL}/ai/analyze-brand-voice`,
        { sampleContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const analysis = response.data;

      // Update settings with AI analysis
      setSettings(prev => ({
        ...prev,
        brand_voice_tone: analysis.tone || prev.brand_voice_tone,
        brand_voice_description: analysis.voiceDescription || prev.brand_voice_description,
        key_messages: analysis.keyMessages || prev.key_messages,
        target_audience_description: analysis.targetAudience || prev.target_audience_description
      }));

      toast.success('Brand voice analyzed! Review and adjust the suggestions below.');
      setShowAIAnalyzer(false);
      setSampleContent('');
    } catch (error: any) {
      console.error('Error analyzing brand voice:', error);
      toast.error(error.response?.data?.error || 'Failed to analyze brand voice');
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading brand settings...</p>
        </div>
      </div>
    );
  }

  // Free tier - show upgrade prompt
  if (user?.tier === 'free') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <nav className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div
                onClick={() => navigate('/dashboard')}
                className="cursor-pointer"
              >
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                  The Course Creator's Vault
                </h1>
                <p className="text-[10px] sm:text-xs text-gray-500">by Templimize AI</p>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back to Dashboard
              </button>
            </div>
          </div>
        </nav>

        {/* Upgrade Prompt */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-2xl shadow-xl p-12 text-center">
            <div className="text-8xl mb-6">🎨</div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Brand Customization is a Premium Feature
            </h1>
            <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
              Create and store your brand identity - logo, colors, fonts, and brand voice - then apply it automatically across all your courses and marketing materials!
            </p>

            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
              <div className="bg-white rounded-xl shadow-md p-6 border-2 border-blue-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Basic Plan</h3>
                <p className="text-3xl font-bold text-blue-600 mb-4">$29<span className="text-lg text-gray-600">/mo</span></p>
                <ul className="text-left space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    Upload & store logo
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    4 brand colors
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    2 fonts (primary + secondary)
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-xl shadow-md p-6 border-2 border-purple-400 relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                  RECOMMENDED
                </div>
                <h3 className="text-2xl font-bold mb-4">Premium Plan</h3>
                <p className="text-3xl font-bold mb-4">$47<span className="text-lg opacity-90">/mo</span></p>
                <ul className="text-left space-y-2">
                  <li className="flex items-start">
                    <span className="text-yellow-300 mr-2">✓</span>
                    Everything in Basic
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-300 mr-2">✓</span>
                    Unlimited colors
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-300 mr-2">✓</span>
                    Multiple fonts
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-300 mr-2">✓</span>
                    Brand voice & messaging
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-300 mr-2">✓</span>
                    Auto-apply everywhere
                  </li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => navigate('/pricing')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-12 py-4 rounded-full text-xl font-bold hover:shadow-2xl transform hover:scale-105 transition"
            >
              View Pricing & Upgrade →
            </button>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  const isBasic = user?.tier === 'basic';
  const isPremium = user?.tier === 'premium';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back
              </button>
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                Brand Customization
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {isBasic && 'Basic Plan'}
                {isPremium && 'Premium Plan'}
              </span>
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tier Info Banner */}
        {isBasic && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">You're on the Basic Plan</h3>
                <p className="text-gray-700">Includes: Logo + 4 colors + 2 fonts</p>
              </div>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition"
              >
                Upgrade to Premium →
              </button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Logo Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🖼️</span>
              Brand Logo
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo URL
                </label>
                <input
                  type="url"
                  value={settings.logo_url || ''}
                  onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                  placeholder="https://example.com/logo.png"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent transition"
                />
                <p className="text-xs text-gray-500 mt-1">Enter a public URL to your logo image</p>
              </div>
              {settings.logo_url && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <img
                    src={settings.logo_url}
                    alt="Brand Logo"
                    className="max-h-32 mx-auto"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Colors Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🎨</span>
              Brand Colors
              {isBasic && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">4 Colors</span>}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={settings.primary_color || '#667eea'}
                      onChange={(e) => handleColorChange('primary_color', e.target.value)}
                      className="h-12 w-16 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings.primary_color || '#667eea'}
                      onChange={(e) => handleColorChange('primary_color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={settings.secondary_color || '#764ba2'}
                      onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                      className="h-12 w-16 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings.secondary_color || '#764ba2'}
                      onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={settings.accent_color || '#f59e0b'}
                      onChange={(e) => handleColorChange('accent_color', e.target.value)}
                      className="h-12 w-16 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings.accent_color || '#f59e0b'}
                      onChange={(e) => handleColorChange('accent_color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={settings.background_color || '#ffffff'}
                      onChange={(e) => handleColorChange('background_color', e.target.value)}
                      className="h-12 w-16 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings.background_color || '#ffffff'}
                      onChange={(e) => handleColorChange('background_color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                    />
                  </div>
                </div>
              </div>

              {isPremium && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-purple-600 font-semibold mb-2">Premium: Add unlimited colors!</p>
                  <button className="text-sm text-purple-600 hover:text-purple-700 font-semibold">
                    + Add Additional Color
                  </button>
                </div>
              )}

              {isBasic && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                  <p className="text-gray-700">
                    Want more colors? <button onClick={() => navigate('/pricing')} className="text-purple-600 font-semibold hover:underline">Upgrade to Premium</button>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Fonts Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">✒️</span>
              Typography
              {isBasic && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">2 Fonts</span>}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary Font</label>
                <select
                  value={settings.primary_font || 'Roboto'}
                  onChange={(e) => setSettings({ ...settings, primary_font: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  style={{ fontFamily: settings.primary_font || 'Roboto' }}
                >
                  {popularFonts.map(font => (
                    <option key={font} value={font} style={{ fontFamily: font }}>
                      {font}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Font</label>
                <select
                  value={settings.secondary_font || ''}
                  onChange={(e) => setSettings({ ...settings, secondary_font: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                >
                  <option value="">Select a font...</option>
                  {popularFonts.map(font => (
                    <option key={font} value={font} style={{ fontFamily: font }}>
                      {font}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Use for body text, captions, or contrast with your primary font</p>
              </div>

              {isPremium && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-purple-600 font-semibold mb-2">Premium: Add unlimited fonts!</p>
                  <button className="text-sm text-purple-600 hover:text-purple-700 font-semibold">
                    + Add Additional Font
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Brand Voice Section - Premium Only */}
          {isPremium ? (
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <span className="mr-2">💬</span>
                  Brand Voice
                  <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Premium</span>
                </h3>
                <button
                  onClick={() => setShowAIAnalyzer(true)}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:shadow-lg transition flex items-center gap-2"
                >
                  <span>🤖</span> AI Analyze
                </button>
              </div>

              {/* AI Brand Voice Analyzer Modal */}
              {showAIAnalyzer && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <span>🤖</span> AI Brand Voice Analyzer
                      </h3>
                      <button
                        onClick={() => setShowAIAnalyzer(false)}
                        className="text-gray-500 hover:text-gray-700 text-2xl"
                      >
                        &times;
                      </button>
                    </div>

                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-purple-800 mb-2">How it works:</h4>
                      <ol className="text-sm text-purple-700 space-y-1 list-decimal list-inside">
                        <li>Paste sample content from your website, emails, or social media</li>
                        <li>AI analyzes your writing style, tone, and key themes</li>
                        <li>Get auto-generated brand voice settings to review and adjust</li>
                        <li>Your brand voice will be used across all AI-generated content!</li>
                      </ol>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Paste Your Sample Content
                        </label>
                        <textarea
                          value={sampleContent}
                          onChange={(e) => setSampleContent(e.target.value)}
                          rows={8}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent resize-none"
                          placeholder="Paste content from your website 'About' page, email newsletters, social media posts, or any other content that represents your brand voice...

Example: 'Hey there, fellow entrepreneur! I'm Sarah, and I help busy business owners create passive income through online courses. My approach is all about keeping things simple, actionable, and fun. No corporate jargon here - just real talk and strategies that actually work...'"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          The more content you provide, the better the analysis. Try 200+ words for best results.
                        </p>
                      </div>

                      <button
                        onClick={analyzeWithAI}
                        disabled={analyzing || !sampleContent.trim()}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-bold hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {analyzing ? (
                          <>
                            <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                            Analyzing Brand Voice...
                          </>
                        ) : (
                          <>
                            <span>✨</span> Analyze My Brand Voice
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tone</label>
                  <select
                    value={settings.brand_voice_tone || ''}
                    onChange={(e) => setSettings({ ...settings, brand_voice_tone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  >
                    <option value="">Select tone...</option>
                    <option value="professional">Professional</option>
                    <option value="friendly">Friendly</option>
                    <option value="casual">Casual</option>
                    <option value="authoritative">Authoritative</option>
                    <option value="inspirational">Inspirational</option>
                    <option value="educational">Educational</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Brand Voice Description</label>
                  <textarea
                    value={settings.brand_voice_description || ''}
                    onChange={(e) => setSettings({ ...settings, brand_voice_description: e.target.value })}
                    placeholder="Describe your brand's personality and communication style..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Key Messages</label>
                  <div className="space-y-2">
                    {(settings.key_messages || []).map((message, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={message}
                          onChange={(e) => updateKeyMessage(index, e.target.value)}
                          placeholder="Key message..."
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                        />
                        <button
                          onClick={() => removeKeyMessage(index)}
                          className="text-red-600 hover:text-red-700 font-semibold"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addKeyMessage}
                      className="text-purple-600 hover:text-purple-700 font-semibold text-sm"
                    >
                      + Add Key Message
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
                  <textarea
                    value={settings.target_audience_description || ''}
                    onChange={(e) => setSettings({ ...settings, target_audience_description: e.target.value })}
                    placeholder="Describe your target audience..."
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent resize-none"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl shadow-lg p-8 text-center">
              <div className="text-6xl mb-4">🔒</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Brand Voice is Premium</h3>
              <p className="text-gray-700 mb-6">
                Define your brand's tone, key messages, and target audience. AI will use this to customize all your content!
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-xl transition"
              >
                Upgrade to Premium
              </button>
            </div>
          )}
        </div>

        {/* Save Button (Mobile) */}
        <div className="mt-8 flex justify-center md:hidden">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-12 py-4 rounded-full text-lg font-bold hover:shadow-2xl transform hover:scale-105 transition disabled:opacity-50 w-full max-w-md"
          >
            {saving ? 'Saving Changes...' : 'Save Brand Settings'}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BrandSettings;
